import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ArrowsPointingOutIcon, XMarkIcon } from '@heroicons/react/24/outline'

/** Minimal API surface (Mermaid loaded from CDN, not npm). */
export type MermaidApi = {
  initialize: (config: Record<string, unknown>) => void
  render: (id: string, text: string) => Promise<{ svg: string }>
}

declare global {
  interface Window {
    mermaid?: MermaidApi
  }
}

/** Bump version to bust cached broken CDN + pick layout fixes. */
const MERMAID_CDN = 'https://cdn.jsdelivr.net/npm/mermaid@11.6.0/dist/mermaid.min.js'
const MERMAID_CDN_FLAG = '11.6.0'

let mermaidLoader: Promise<MermaidApi> | null = null

export function loadMermaidFromCdn(): Promise<MermaidApi> {
  if (typeof window !== 'undefined' && window.mermaid) {
    const tag = document.querySelector<HTMLScriptElement>('script[data-askai-mmd-cdn]')
    if (tag?.dataset.askaiMmdCdn === MERMAID_CDN_FLAG) {
      return Promise.resolve(window.mermaid)
    }
    // Stale CDN build — force reload
    tag?.remove()
    delete window.mermaid
    mermaidLoader = null
  }
  if (mermaidLoader) return mermaidLoader

  mermaidLoader = new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Mermaid chỉ chạy trên trình duyệt'))
      return
    }

    const finish = () => {
      if (window.mermaid) resolve(window.mermaid)
      else reject(new Error('Mermaid không khởi tạo được'))
    }

    const existing = document.querySelector<HTMLScriptElement>('script[data-askai-mmd-cdn]')
    if (existing) {
      if (existing.dataset.askaiMmdCdn !== MERMAID_CDN_FLAG) {
        existing.remove()
      } else if (window.mermaid) {
        finish()
        return
      } else {
        existing.addEventListener('load', finish)
        existing.addEventListener('error', () => reject(new Error('Tải Mermaid thất bại')))
        return
      }
    }

    const script = document.createElement('script')
    script.src = MERMAID_CDN
    script.async = true
    script.dataset.askaiMmdCdn = MERMAID_CDN_FLAG
    script.onload = finish
    script.onerror = () => {
      mermaidLoader = null
      reject(new Error('Tải Mermaid thất bại'))
    }
    document.head.appendChild(script)
  })

  return mermaidLoader
}

const MERMAID_START =
  /^(sequenceDiagram|flowchart|graph\s|classDiagram|stateDiagram|erDiagram|journey|gantt|pie|mindmap|timeline|quadrantChart|sankey|xychart|requirementDiagram|C4Context)\b/m

/** Strip optional ``` / ```mermaid fences around source. */
export function normalizeMermaidSource(raw: string): string {
  let t = raw.replace(/\u00a0/g, ' ').trim()
  const fence = /^```(?:mermaid)?\s*\r?\n?([\s\S]*?)```$/i.exec(t)
  if (fence) t = fence[1].replace(/\u00a0/g, ' ').trim()
  return t
}

/**
 * Prefer innerText so Quill/HTML `<br>` become real newlines.
 * textContent drops `<br>` → lines glue (`:::systemC[…]`) and Dagre collapses.
 */
export function extractMermaidTextFromElement(el: HTMLElement): string {
  const raw = (el.innerText || el.textContent || '').replace(/\u00a0/g, ' ')
  return raw.trim()
}

/**
 * Escape `&` in edge labels; prefer `flowchart`; un-glue `:::class` stuck to next node id
 * when HTML extraction lost newlines.
 */
export function sanitizeMermaidSource(raw: string): string {
  let t = raw
  t = t.replace(/^(graph)(\s+)/im, 'flowchart$2')
  // :::systemC[Label] → :::system\nC[Label]  (class glued to next node)
  t = t.replace(/(:::[A-Za-z_][\w-]*)(?=[A-Z][A-Za-z0-9_]*\s*[\[\(\{])/g, '$1\n')
  // classDef ...;C[ → classDef ...;\nC[
  t = t.replace(/(;)\s*(?=[A-Z][A-Za-z0-9_]*\s*[\[\(\{])/g, '$1\n')
  t = t.replace(/\|([^|]*)\|/g, (_m, label: string) => `|${String(label).replace(/&/g, '#amp;')}|`)
  return t
}

export function looksLikeMermaid(code: string, lang?: string) {
  if (lang === 'mermaid') return true
  const normalized = normalizeMermaidSource(code)
  return MERMAID_START.test(normalized)
}

/** High-contrast light theme — dark text on light fills (readable on posts & slides). */
export const MERMAID_LIGHT_THEME = {
  startOnLoad: false,
  securityLevel: 'loose' as const,
  theme: 'base' as const,
  // SVG <text> labels — avoids foreignObject measurement failures under page CSS
  flowchart: {
    htmlLabels: false,
    useMaxWidth: true,
    nodeSpacing: 50,
    rankSpacing: 60,
    padding: 16,
  },
  themeVariables: {
    darkMode: false,
    background: '#ffffff',
    fontFamily: 'ui-sans-serif, system-ui, sans-serif',
    fontSize: '16px',
    primaryColor: '#e8f5e9',
    primaryTextColor: '#0f172a',
    primaryBorderColor: '#0f3e17',
    secondaryColor: '#f1f5f9',
    secondaryTextColor: '#0f172a',
    secondaryBorderColor: '#64748b',
    tertiaryColor: '#ffffff',
    tertiaryTextColor: '#0f172a',
    tertiaryBorderColor: '#94a3b8',
    lineColor: '#334155',
    textColor: '#0f172a',
    mainBkg: '#ffffff',
    nodeBkg: '#ffffff',
    nodeBorder: '#0f3e17',
    clusterBkg: '#f8fafc',
    clusterBorder: '#94a3b8',
    titleColor: '#0f172a',
    edgeLabelBackground: '#ffffff',
    actorBkg: '#e8f5e9',
    actorBorder: '#0f3e17',
    actorTextColor: '#0f172a',
    actorLineColor: '#334155',
    signalColor: '#334155',
    signalTextColor: '#0f172a',
    labelBoxBkgColor: '#ffffff',
    labelBoxBorderColor: '#94a3b8',
    labelTextColor: '#0f172a',
    loopTextColor: '#0f172a',
    noteBkgColor: '#fffbeb',
    noteTextColor: '#0f172a',
    noteBorderColor: '#d97706',
    activationBkgColor: '#e2e8f0',
    sequenceNumberColor: '#0f172a',
  },
}

function ensureMermaidLayoutCss() {
  if (typeof document === 'undefined') return
  if (document.querySelector('style[data-m2-mermaid-layout]')) return
  const style = document.createElement('style')
  style.dataset.m2MermaidLayout = '1'
  style.textContent = `
    .m2-mermaid-root svg {
      max-width: 100%;
      height: auto;
    }
  `
  document.head.appendChild(style)
}

/** Serialize renders across blocks. */
let renderQueue: Promise<unknown> = Promise.resolve()
let renderIdCounter = 0

function enqueueMermaidRender<T>(fn: () => Promise<T>): Promise<T> {
  const run = renderQueue.then(fn, fn)
  renderQueue = run.then(
    () => undefined,
    () => undefined
  )
  return run
}

/**
 * Render inside a blank iframe so parent Tailwind/CSS cannot collapse Dagre layout
 * (nodes stacking into one circle). Returns SVG markup string.
 */
function renderMermaidInIframe(text: string): Promise<string> {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Mermaid chỉ chạy trên trình duyệt'))
      return
    }

    const cb = `__m2mmd_${Date.now()}_${++renderIdCounter}`
    const iframe = document.createElement('iframe')
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin')
    iframe.setAttribute('aria-hidden', 'true')
    iframe.style.cssText =
      'position:absolute;left:-20000px;top:0;width:1400px;height:1000px;border:0;opacity:0;pointer-events:none'

    let settled = false
    const finish = (err?: Error, svg?: string) => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      try {
        delete (window as unknown as Record<string, unknown>)[cb]
      } catch {
        /* ignore */
      }
      iframe.remove()
      if (err) reject(err)
      else if (svg) resolve(svg)
      else reject(new Error('Mermaid không trả về SVG'))
    }

    const timer = window.setTimeout(() => finish(new Error('Mermaid render timeout')), 45000)

    ;(window as unknown as Record<string, unknown>)[cb] = (payload: {
      svg?: string
      error?: string
    }) => {
      if (payload?.error) finish(new Error(payload.error))
      else finish(undefined, payload?.svg)
    }

    document.body.appendChild(iframe)
    const doc = iframe.contentDocument
    if (!doc) {
      finish(new Error('Không tạo được iframe Mermaid'))
      return
    }

    const renderId = `iso-${renderIdCounter}`
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<style>html,body{margin:0;background:#fff;font-family:ui-sans-serif,system-ui,sans-serif}#root{width:1400px;min-height:900px}</style>
</head><body><div id="root"></div>
<script src="${MERMAID_CDN}"><\/script>
<script>
(async function () {
  try {
    if (!window.mermaid) throw new Error('Mermaid không tải được trong iframe');
    window.mermaid.initialize({
      startOnLoad: false,
      securityLevel: 'loose',
      theme: 'base',
      flowchart: {
        htmlLabels: false,
        useMaxWidth: false,
        nodeSpacing: 50,
        rankSpacing: 60,
        padding: 16
      },
      themeVariables: {
        darkMode: false,
        background: '#ffffff',
        primaryTextColor: '#0f172a',
        textColor: '#0f172a',
        lineColor: '#334155',
        edgeLabelBackground: '#ffffff'
      }
    });
    var out = await window.mermaid.render(${JSON.stringify(renderId)}, ${JSON.stringify(text)});
    parent[${JSON.stringify(cb)}]({ svg: out.svg });
  } catch (e) {
    parent[${JSON.stringify(cb)}]({ error: (e && e.message) ? e.message : String(e) });
  }
})();
<\/script></body></html>`

    doc.open()
    doc.write(html)
    doc.close()
  })
}

/** True when flowchart nodes all share (nearly) the same translate — classic collapse. */
function svgNodesCollapsed(svg: string): boolean {
  const nodeTransforms = [
    ...svg.matchAll(/class="node[^"]*"[^>]*transform="translate\(([^)]+)\)"/g),
    ...svg.matchAll(/transform="translate\(([^)]+)\)"[^>]*class="node[^"]*"/g),
  ].map((m) => m[1].replace(/\s+/g, ''))
  const unique = new Set(nodeTransforms)
  return nodeTransforms.length >= 3 && unique.size <= 1
}

/** Post-process SVG for readable contrast: light box fills + dark labels. */
function ensureMermaidContrast(svg: string): string {
  let out = svg

  // Only lighten SHAPE fills — skip path (often edges / custom shapes)
  const darkFillValue =
    '#0[0-9a-f]{5}|#1[0-9a-f]{5}|#0f172a|#0f3e17|#111827|#1e293b|#020617|#000000|#000|black'

  out = out.replace(
    new RegExp(
      `(<(?:rect|polygon|circle|ellipse)\\b[^>]*?)\\sfill\\s*=\\s*["']?(?:${darkFillValue})["']?`,
      'gi'
    ),
    '$1 fill="#e8f5e9"'
  )

  out = out.replace(
    /(<rect\b[^>]*\bclass\s*=\s*["'][^"']*\bactor\b[^"']*["'][^>]*?)\sfill\s*=\s*["'][^"']*["']/gi,
    '$1 fill="#e8f5e9"'
  )
  out = out.replace(
    /(<rect\b[^>]*?)\sfill\s*=\s*["'][^"']*["']([^>]*\bclass\s*=\s*["'][^"']*\bactor\b)/gi,
    '$1 fill="#e8f5e9"$2'
  )

  out = out.replace(/<text\b([^>]*)>/gi, (_full, attrs: string) => {
    let next = String(attrs)
    if (/\bfill\s*=/i.test(next)) {
      next = next.replace(/\bfill\s*=\s*["'][^"']*["']/gi, 'fill="#0f172a"')
    } else {
      next += ' fill="#0f172a"'
    }
    if (/style\s*=/i.test(next)) {
      next = next.replace(/fill\s*:\s*[^;|"']+/gi, 'fill:#0f172a')
    }
    return `<text${next}>`
  })

  out = out.replace(
    /(<foreignObject\b[^>]*>)([\s\S]*?)(<\/foreignObject>)/gi,
    (_m, open: string, inner: string, close: string) => {
      const patched = inner
        .replace(/color\s*:\s*[^;]+/gi, 'color:#0f172a')
        .replace(/<div\b(?![^>]*style=)/gi, '<div style="color:#0f172a"')
      return `${open}${patched}${close}`
    }
  )

  return out
}

export const MERMAID_WRAP_CLASS =
  'm2-mermaid-root not-prose my-3 overflow-x-auto rounded-lg border border-slate-200 bg-white text-[#0f172a] ' +
  '[&_svg]:mx-auto [&_svg]:h-auto [&_svg]:max-w-full ' +
  '[&_text]:!fill-[#0f172a] [&_text.actor]:!fill-[#0f172a] [&_.messageText]:!fill-[#0f172a] ' +
  '[&_rect.actor]:!fill-[#e8f5e9] [&_rect.actor-top]:!fill-[#e8f5e9] [&_rect.actor-bottom]:!fill-[#e8f5e9] [&_rect.actor-man]:!fill-[#e8f5e9]'

type MermaidBlockProps = {
  code: string
  idPrefix?: string
}

export function MermaidBlock({ code, idPrefix: _idPrefix = 'mmd' }: MermaidBlockProps) {
  const [rendered, setRendered] = useState(false)
  const [svg, setSvg] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const renderSeq = useRef(0)
  const autoTried = useRef(false)
  const source = normalizeMermaidSource(code)

  const handleRender = async () => {
    setBusy(true)
    setError(null)
    const seq = ++renderSeq.current
    try {
      ensureMermaidLayoutCss()
      const text = sanitizeMermaidSource(source)
      const svgOut = await enqueueMermaidRender(async () => {
        let out = await renderMermaidInIframe(text)
        // Extremely rare: if still collapsed, retry once with a fresh iframe
        if (svgNodesCollapsed(out)) {
          out = await renderMermaidInIframe(text)
        }
        return out
      })
      if (seq !== renderSeq.current) return
      setSvg(ensureMermaidContrast(svgOut))
      setRendered(true)
    } catch (e: unknown) {
      if (seq !== renderSeq.current) return
      setError(e instanceof Error ? e.message : 'Không render được sơ đồ')
      setRendered(false)
      setSvg(null)
    } finally {
      if (seq === renderSeq.current) setBusy(false)
    }
  }

  useEffect(() => {
    autoTried.current = false
    setRendered(false)
    setSvg(null)
    setError(null)
    setFullscreen(false)
  }, [source])

  useEffect(() => {
    if (autoTried.current) return
    autoTried.current = true
    void handleRender()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source])

  useEffect(() => {
    if (!fullscreen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false)
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [fullscreen])

  const diagramSvgClass =
    'overflow-x-auto bg-white p-4 text-[#0f172a] [&_svg]:mx-auto [&_svg]:max-w-full [&_text]:!fill-[#0f172a] [&_text.actor]:!fill-[#0f172a] [&_rect.actor]:!fill-[#e8f5e9]'

  return (
    <div className={MERMAID_WRAP_CLASS}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-800">
          Diagram
        </span>
        <div className="flex items-center gap-2">
          {rendered && svg && (
            <button
              type="button"
              onClick={() => setFullscreen(true)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
              aria-label="Xem toàn màn hình"
              title="Xem toàn màn hình"
            >
              <ArrowsPointingOutIcon className="h-4 w-4" />
            </button>
          )}
          {rendered && (
            <button
              type="button"
              onClick={() => {
                setRendered(false)
                setSvg(null)
                setError(null)
              }}
              className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-800 hover:bg-slate-50"
            >
              Xem code
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleRender()}
            className="rounded-md border border-m2-primary/30 bg-m2-primary/10 px-2.5 py-1 text-xs font-semibold text-m2-primary disabled:opacity-50"
          >
            {busy ? 'Đang render…' : rendered ? 'Render lại' : 'Render sơ đồ'}
          </button>
        </div>
      </div>
      {error && (
        <div className="border-b border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">{error}</div>
      )}
      {rendered && svg ? (
        <div className={diagramSvgClass} dangerouslySetInnerHTML={{ __html: svg }} />
      ) : (
        <pre className="overflow-x-auto bg-slate-50 p-3 text-xs leading-relaxed text-slate-800 border-t border-slate-200">
          <code className="text-slate-800">{source}</code>
        </pre>
      )}

      {fullscreen &&
        svg &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex flex-col bg-black/70 p-3 sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-label="Sơ đồ toàn màn hình"
            onClick={() => setFullscreen(false)}
          >
            <div className="ml-auto mb-2 shrink-0" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setFullscreen(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-800 shadow hover:bg-slate-100"
                aria-label="Đóng"
                title="Đóng (Esc)"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div
              className="min-h-0 flex-1 overflow-auto rounded-lg bg-white p-4 sm:p-6 [&_svg]:mx-auto [&_svg]:h-auto [&_svg]:max-h-none [&_svg]:w-auto [&_svg]:max-w-none [&_text]:!fill-[#0f172a]"
              onClick={(e) => e.stopPropagation()}
              dangerouslySetInnerHTML={{ __html: svg }}
            />
          </div>,
          document.body
        )}
    </div>
  )
}
